import React, { useState, useEffect } from 'react';
import { FiX, FiSave } from 'react-icons/fi';
import { getUsers } from '../../../api/users';
import { createPerformanceTask } from '../../../api/retailPerformanceTasks';
import { toast } from 'react-toastify';

const CreateTaskModal = ({ isOpen, onClose, onSuccess }) => {
  const [loading, setLoading] = useState(false);
  const [users, setUsers] = useState([]);
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    task_type: 'sales_count',
    target_value: '',
    start_time: '',
    end_time: '',
    user_ids: []
  });

  useEffect(() => {
    if (isOpen) {
      fetchUsers();
    }
  }, [isOpen]);

  const fetchUsers = async () => {
    try {
      // Fetch all staff users
      const response = await getUsers({
        role: ['staff', 'branch_staff', 'branch_admin'],
        limit: 1000
      });
      if (response.success) {
        setUsers(response.data || []);
      }
    } catch (error) {
      console.error('Error fetching users:', error);
      toast.error('Failed to load users');
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const payload = {
        ...formData,
        target_value: parseFloat(formData.target_value),
        user_ids: formData.user_ids.map((id) => parseInt(id))
      };

      const response = await createPerformanceTask(payload);
      if (response.success) {
        toast.success('Performance task created successfully');
        onSuccess();
        setFormData({
          title: '',
          description: '',
          task_type: 'sales_count',
          target_value: '',
          start_time: '',
          end_time: '',
          user_ids: []
        });
      }
    } catch (error) {
      toast.error(error.response?.data?.message || 'Failed to create task');
    } finally {
      setLoading(false);
    }
  };

  const toggleUser = (userId) => {
    setFormData((prev) => {
      const ids = prev.user_ids.includes(userId)
        ? prev.user_ids.filter((id) => id !== userId)
        : [...prev.user_ids, userId];
      return { ...prev, user_ids: ids };
    });
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      <div className="flex items-center justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0">
        <div className="fixed inset-0 transition-opacity" aria-hidden="true">
          <div className="absolute inset-0 bg-gray-500 opacity-75" onClick={onClose}></div>
        </div>

        <span className="hidden sm:inline-block sm:align-middle sm:h-screen" aria-hidden="true">
          &#8203;
        </span>

        <div className="inline-block align-bottom bg-white dark:bg-gray-800 rounded-lg text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-2xl sm:w-full">
          <div className="bg-white dark:bg-gray-800 px-4 pt-5 pb-4 sm:p-6 sm:pb-4">
            <div className="flex justify-between items-center mb-5">
              <h3 className="text-lg font-medium leading-6 text-gray-900 dark:text-white">
                Create Performance Task
              </h3>
              <button onClick={onClose} className="text-gray-400 hover:text-gray-500">
                <FiX className="h-6 w-6" />
              </button>
            </div>

            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                  Task Title *
                </label>
                <input
                  type="text"
                  required
                  className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-accent-gold focus:border-accent-gold sm:text-sm dark:bg-gray-700 dark:border-gray-600 dark:text-white"
                  value={formData.title}
                  onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                  Description
                </label>
                <textarea
                  className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-accent-gold focus:border-accent-gold sm:text-sm dark:bg-gray-700 dark:border-gray-600 dark:text-white"
                  rows="3"
                  value={formData.description}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                />
              </div>

              <div className="grid grid-cols-1 gap-y-6 gap-x-4 sm:grid-cols-2">
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                    Task Type *
                  </label>
                  <select
                    className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-accent-gold focus:border-accent-gold sm:text-sm dark:bg-gray-700 dark:border-gray-600 dark:text-white"
                    value={formData.task_type}
                    onChange={(e) => setFormData({ ...formData, task_type: e.target.value })}
                  >
                    <option value="sales_count">Sales Count</option>
                    <option value="sales_amount">Sales Amount (Revenue)</option>
                    <option value="tiles_quantity">Tiles Quantity</option>
                    <option value="accessories_quantity">Accessories Quantity</option>
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                    Target Value *
                  </label>
                  <input
                    type="number"
                    required
                    min="1"
                    step="0.01"
                    className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-accent-gold focus:border-accent-gold sm:text-sm dark:bg-gray-700 dark:border-gray-600 dark:text-white"
                    value={formData.target_value}
                    onChange={(e) => setFormData({ ...formData, target_value: e.target.value })}
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                    Start Time *
                  </label>
                  <input
                    type="datetime-local"
                    required
                    className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-accent-gold focus:border-accent-gold sm:text-sm dark:bg-gray-700 dark:border-gray-600 dark:text-white"
                    value={formData.start_time}
                    onChange={(e) => setFormData({ ...formData, start_time: e.target.value })}
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                    End Time *
                  </label>
                  <input
                    type="datetime-local"
                    required
                    className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-accent-gold focus:border-accent-gold sm:text-sm dark:bg-gray-700 dark:border-gray-600 dark:text-white"
                    value={formData.end_time}
                    onChange={(e) => setFormData({ ...formData, end_time: e.target.value })}
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Assign Users *
                </label>
                <div className="border border-gray-300 rounded-md max-h-48 overflow-y-auto p-2 dark:bg-gray-700 dark:border-gray-600">
                  {users.map((user) => (
                    <div key={user.id} className="flex items-center py-1">
                      <input
                        id={`user-${user.id}`}
                        type="checkbox"
                        className="h-4 w-4 text-accent-gold focus:ring-accent-gold border-gray-300 rounded"
                        checked={formData.user_ids.includes(user.id)}
                        onChange={() => toggleUser(user.id)}
                      />
                      <label
                        htmlFor={`user-${user.id}`}
                        className="ml-2 block text-sm text-gray-900 dark:text-white"
                      >
                        {user.full_name} ({user.role})
                      </label>
                    </div>
                  ))}
                  {users.length === 0 && <p className="text-sm text-gray-500">No staff found.</p>}
                </div>
                {formData.user_ids.length === 0 && (
                  <p className="text-xs text-red-500 mt-1">Please select at least one user</p>
                )}
              </div>

              <div className="mt-5 sm:mt-6 sm:grid sm:grid-cols-2 sm:gap-3 sm:grid-flow-row-dense">
                <button
                  type="submit"
                  disabled={loading || formData.user_ids.length === 0}
                  className="w-full inline-flex justify-center rounded-md border border-transparent shadow-sm px-4 py-2 bg-accent-gold text-base font-medium text-white hover:bg-accent-goldDark focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-accent-gold sm:col-start-2 sm:text-sm disabled:opacity-50"
                >
                  {loading ? 'Creating...' : 'Create Task'}
                </button>
                <button
                  type="button"
                  onClick={onClose}
                  className="mt-3 w-full inline-flex justify-center rounded-md border border-gray-300 shadow-sm px-4 py-2 bg-white text-base font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-accent-gold sm:mt-0 sm:col-start-1 sm:text-sm dark:bg-gray-700 dark:text-white dark:border-gray-600 dark:hover:bg-gray-600"
                >
                  Cancel
                </button>
              </div>
            </form>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CreateTaskModal;
